import { DocumentDownloadIcon, TrashAltIcon } from "assets/icons";
import { FilesUploader } from "components";
import {
  DOCUMENT_FORMATS,
  IMAGE_FORMATS,
  MAX_DOCUMENT_FILE_SIZE,
  VIDEO_FORMATS
} from "constants/common";
import { MediaDirectoryKeys } from "constants/enums";
import { getAcceptObjectForFilesInput } from "helpers";
import { DirectUploadMedia } from "libs/directUpload";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { DateInput, FormField, Input, Modal, Select } from "ui";

import {
  CompanyLegalDocument,
  CompanyLegalDocumentType,
  useCreateCompanyDoc,
  useUpdateCompanyDoc
} from "../../../apis";

import styles from "./LegalDocumentModal.module.scss";

const FORM_ID = "legal-document-form";

interface FormValues {
  type: CompanyLegalDocumentType | "";
  name: string;
  issuedAt: Date | null;
  expiresAt: Date | null;
  mediaId: string | null;
}

const TYPE_OPTIONS = [
  {
    value: CompanyLegalDocumentType.RccmRegistration,
    label: "RCCM registration"
  },
  {
    value: CompanyLegalDocumentType.BusinessOperatingCertificate,
    label: "Business operating certificate"
  },
  {
    value: CompanyLegalDocumentType.VehicleInsurance,
    label: "Vehicle insurance — fleet policy"
  },
  {
    value: CompanyLegalDocumentType.TaxCertificate,
    label: "Tax certificate"
  },
  { value: CompanyLegalDocumentType.Other, label: "Other" }
];

interface LegalDocumentModalProps {
  isOpen: boolean;
  companyId: string;
  document?: CompanyLegalDocument | null;
  onClose: () => void;
}

const toIsoDate = (d: Date | null) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const UploadCloudIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 18 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 10C1.55228 10 2 10.4477 2 11V19C2 19.2652 2.10536 19.5196 2.29289 19.7071C2.48043 19.8946 2.73478 20 3 20H15C15.2652 20 15.5196 19.8946 15.7071 19.7071C15.8946 19.5196 16 19.2652 16 19V11C16 10.4477 16.4477 10 17 10C17.5523 10 18 10.4477 18 11V19C18 19.7957 17.6839 20.5587 17.1213 21.1213C16.5587 21.6839 15.7957 22 15 22H3C2.20435 22 1.44129 21.6839 0.87868 21.1213C0.31607 20.5587 0 19.7956 0 19V11C0 10.4477 0.447715 10 1 10Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.29289 0.292893C8.68342 -0.0976311 9.31658 -0.0976311 9.70711 0.292893L13.7071 4.29289C14.0976 4.68342 14.0976 5.31658 13.7071 5.70711C13.3166 6.09763 12.6834 6.09763 12.2929 5.70711L9 2.41421L5.70711 5.70711C5.31658 6.09763 4.68342 6.09763 4.29289 5.70711C3.90237 5.31658 3.90237 4.68342 4.29289 4.29289L8.29289 0.292893Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 0C9.55228 0 10 0.447715 10 1V14C10 14.5523 9.55228 15 9 15C8.44772 15 8 14.5523 8 14V1C8 0.447715 8.44772 0 9 0Z"
      fill="currentColor"
    />
  </svg>
);

interface DropzoneProps {
  onDrop: (files: File[]) => void;
}

const Dropzone = ({ onDrop }: DropzoneProps) => {
  const { t } = useTranslation();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: getAcceptObjectForFilesInput({
      acceptImageFormats: IMAGE_FORMATS,
      acceptVideoFormats: VIDEO_FORMATS,
      acceptDocumentFormats: DOCUMENT_FORMATS,
      isImagesAllowed: true,
      isVideosAllowed: false,
      isDocumentsAllowed: true
    }),
    maxFiles: 1,
    maxSize: MAX_DOCUMENT_FILE_SIZE,
    onDropAccepted: onDrop,
    onDropRejected: (rejections) => {
      const message = rejections[0]?.errors[0]?.message;
      toast.error(message || t("File rejected"));
    }
  });

  const className = isDragActive
    ? `${styles.dropzone} ${styles["dropzone--active"]}`
    : styles.dropzone;

  return (
    <div {...getRootProps({ className })}>
      <input {...getInputProps()} />
      <span className={styles.dropzone__icon}>
        <UploadCloudIcon />
      </span>
      <p className={styles.dropzone__text}>
        {t("Drop here or ")}
        <span className={styles.dropzone__textBrowse}>{t("browse")}</span>
      </p>
      <p className={styles.dropzone__hint}>
        {t("PDF, JPG, or PNG • up to 10 MB")}
      </p>
    </div>
  );
};

interface PreviewProps {
  media: DirectUploadMedia;
  onRemove: () => void;
}

const Preview = ({ media, onRemove }: PreviewProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.preview}>
      <span className={styles.preview__icon}>
        <DocumentDownloadIcon />
      </span>
      <span className={styles.preview__name}>
        {media.fileName || media.url || t("Uploaded file")}
      </span>
      <button
        type="button"
        className={styles.preview__remove}
        onClick={onRemove}
        aria-label={t("Remove file")}
      >
        <TrashAltIcon width={16} height={16} fill="currentColor" />
      </button>
    </div>
  );
};

const LegalDocumentModal = ({
  isOpen,
  companyId,
  document,
  onClose
}: LegalDocumentModalProps) => {
  const { t } = useTranslation();

  const isEdit = !!document;

  const [media, setMedia] = useState<DirectUploadMedia | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      type: "",
      name: "",
      issuedAt: null,
      expiresAt: null,
      mediaId: null
    }
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      type: document?.type ?? "",
      name: document?.name ?? "",
      issuedAt: document?.issuedAt ? new Date(document.issuedAt) : null,
      expiresAt: document?.expiresAt ? new Date(document.expiresAt) : null,
      mediaId: document?.mediaId ?? null
    });
    setMedia(
      document?.media
        ? ({
            id: document.media.id,
            url: document.media.url,
            fileName: document.name
          } as DirectUploadMedia)
        : null
    );
  }, [isOpen, document, reset]);

  const { mutate: createDoc, isLoading: isCreating } = useCreateCompanyDoc(
    companyId,
    {
      onSuccess: () => {
        toast.success(t("Document uploaded"));
        onClose();
      },
      onError: (e) => toast.error(e?.message || t("Failed to upload"))
    }
  );

  const { mutate: updateDoc, isLoading: isUpdating } = useUpdateCompanyDoc(
    companyId,
    {
      onSuccess: () => {
        toast.success(t("Document updated"));
        onClose();
      },
      onError: (e) => toast.error(e?.message || t("Failed to update"))
    }
  );

  const onMediaUploaded = (m: DirectUploadMedia) => {
    setMedia(m);
    setValue("mediaId", m.id);
  };

  const onMediaRemoved = () => {
    setMedia(null);
    setValue("mediaId", null);
  };

  const onSubmit = (values: FormValues) => {
    if (!values.type) return;
    if (isEdit && document) {
      updateDoc({
        id: document.id,
        body: {
          mediaId: values.mediaId ?? undefined,
          type: values.type as CompanyLegalDocumentType,
          name: values.name,
          issuedAt: toIsoDate(values.issuedAt),
          expiresAt: toIsoDate(values.expiresAt)
        }
      });
      return;
    }
    if (!values.mediaId) return;
    createDoc({
      mediaId: values.mediaId,
      type: values.type as CompanyLegalDocumentType,
      name: values.name,
      issuedAt: toIsoDate(values.issuedAt),
      expiresAt: toIsoDate(values.expiresAt)
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t("Edit legal document") : t("Upload legal document")}
      formId={FORM_ID}
      okText={isEdit ? t("Save changes") : t("Upload document")}
      cancelText={t("Cancel")}
      isLoading={isCreating || isUpdating}
      isOkDisabled={!media}
      className={styles.modal}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FilesUploader
          onSuccessUploading={onMediaUploaded}
          uploadDirectoryKey={MediaDirectoryKeys.DeliveryCompany}
        >
          {({ onFilesDrop }) =>
            media ? (
              <Preview media={media} onRemove={onMediaRemoved} />
            ) : (
              <Dropzone onDrop={onFilesDrop} />
            )
          }
        </FilesUploader>

        <div className={styles.form__fields}>
          <Controller
            control={control}
            name="type"
            rules={{ required: t("Required field") }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormField
                label={t("Document type")}
                errorMessage={error?.message}
              >
                <Select
                  value={value}
                  placeholder={t("Select...")}
                  options={TYPE_OPTIONS.map((o) => ({
                    label: t(o.label),
                    value: o.value
                  }))}
                  onChange={(next) =>
                    onChange(Array.isArray(next) ? next[0] : next)
                  }
                />
              </FormField>
            )}
          />

          <FormField
            label={t("Document name")}
            errorMessage={errors.name?.message}
          >
            <Input
              {...register("name", { required: t("Required field") })}
              placeholder={t("e.g. NSIA Fleet Policy 2026")}
            />
          </FormField>

          <div className={styles.form__row}>
            <Controller
              control={control}
              name="issuedAt"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <DateInput
                  value={value}
                  onChange={onChange}
                  label={t("Issued Date")}
                  errorMessage={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="expiresAt"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <DateInput
                  value={value}
                  onChange={onChange}
                  label={t("Expiry Date")}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default LegalDocumentModal;
